import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';

export default function AdminReports() {
  const { api } = useAuth();

  const [reportType, setReportType] = useState('leads');
  const [sortBy, setSortBy] = useState('date_desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [leadStatusFilter, setLeadStatusFilter] = useState('all');
  const [clientStatusFilter, setClientStatusFilter] = useState('all');

  const clientStatusLabels = {
    new: 'Novo',
    contacted: 'Contatado',
    visit_scheduled: 'Visita Agendada',
    proposal: 'Proposta',
    closed_won: 'Fechado (Ganho)',
    closed_lost: 'Fechado (Perdido)',
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setReportGenerated(false);

    try {
      if (reportType === 'leads') {
        const res = await api.get('/leads');
        setData(res.data || []);
      }

      if (reportType === 'clients') {
        const res = await api.get('/clients');
        const clients = res.data || [];

        const clientsWithInteractions = await Promise.all(
          clients.map(async (client) => {
            try {
              const interactionsRes = await api.get(`/interactions/${client.id}`);
              const interactions = interactionsRes.data || [];

              const lastInteraction =
                interactions.length > 0
                  ? interactions[0]
                  : null;

              return {
                ...client,
                interactions_count: interactions.length,
                last_interaction: lastInteraction?.created_at || null,
                last_interaction_text:
                  lastInteraction?.content ||
                  lastInteraction?.description ||
                  lastInteraction?.notes ||
                  '-',
              };
            } catch (err) {
              return {
                ...client,
                interactions_count: 0,
                last_interaction: null,
                last_interaction_text: '-',
              };
            }
          })
        );

        setData(clientsWithInteractions);
      }

      if (reportType === 'sales') {
        const [clientsRes, propertiesRes] = await Promise.all([
          api.get('/clients'),
          api.get('/properties?limit=1000')
        ]);

        const clients = clientsRes.data || [];
        const properties = propertiesRes.data || [];

        const closedWon = clients.filter(c => c.status === 'closed_won');

        const salesRows = [];

        closedWon.forEach((client) => {
          const propertyIds = client.purchased_property_ids || [];

          propertyIds.forEach((propertyId) => {
            const property = properties.find((p) => p.id === propertyId);

            salesRows.push({
              id: `${client.id}-${propertyId}`,
              client_id: client.id,
              client_name: client.name,
              property_id: property?.id || propertyId,
              property_title: property?.title || '-',
              deal_date: property?.deal_date || property?.updated_at || client.updated_at,
              sale_value: client.sale_value || 0,
              commission_value: client.commission_value || 0,
            });
          });
        });

        setData(salesRows);
      }

      setReportGenerated(true);
    } catch (err) {
      console.error('Erro ao buscar relatório', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setReportGenerated(false);
  }, [reportType, sortBy, startDate, endDate, leadStatusFilter, clientStatusFilter]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const normalizeDateOnly = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-CA');
  };

  const getFilteredData = () => {
    let filtered = [...data];

    if (reportType === 'leads' && leadStatusFilter !== 'all') {
      filtered = filtered.filter((item) => item.status === leadStatusFilter);
    }

    if (reportType === 'clients' && clientStatusFilter !== 'all') {
      filtered = filtered.filter((item) => item.status === clientStatusFilter);
    }

    const getItemDate = (item) => {
      let rawDate = '';

      if (reportType === 'sales') {
        rawDate = item.deal_date;
      } else {
        rawDate = item.created_at;
      }

      if (!rawDate) return '';
      return rawDate.length >= 10 ? rawDate.slice(0, 10) : rawDate;
    };

    if (startDate) {
      filtered = filtered.filter((item) => {
        const itemDate = getItemDate(item);
        return itemDate && itemDate >= startDate;
      });
    }

    if (endDate) {
      filtered = filtered.filter((item) => {
        const itemDate = getItemDate(item);
        return itemDate && itemDate <= endDate;
      });
    }

    if (sortBy === 'date_desc') {
      filtered.sort((a, b) => {
        const dateA = getItemDate(a);
        const dateB = getItemDate(b);
        return dateB.localeCompare(dateA);
      });
    }

    if (sortBy === 'date_asc') {
      filtered.sort((a, b) => {
        const dateA = getItemDate(a);
        const dateB = getItemDate(b);
        return dateA.localeCompare(dateB);
      });
    }

    if (sortBy === 'name_asc') {
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    return filtered;
  };

  
  const getSalesSummary = () => {
    const filtered = getFilteredData();

    const totalSales = filtered.reduce(
      (sum, item) => sum + (item.sale_value || 0),
      0
    );

    const totalCommission = filtered.reduce(
      (sum, item) => sum + (item.commission_value || 0),
      0
    );

    const count = filtered.length;
    const averageTicket = count > 0 ? totalSales / count : 0;

    return {
      count: filtered.length,
      totalSales,
      totalCommission
    };
  };

  const formatDateBR = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">
          Relatórios
        </h1>
        <p className="text-slate-500 mt-1">
          Consulte leads, clientes e vendas por período.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-6 gap-4">
            <div>
              <Label>Data Inicial</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Data Final</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Tipo de Relatório</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="leads">Leads</SelectItem>
                  <SelectItem value="clients">Clientes</SelectItem>
                  <SelectItem value="sales">Vendas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Ordenar por</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Data (mais recente)</SelectItem>
                  <SelectItem value="date_asc">Data (mais antiga)</SelectItem>
                  <SelectItem value="name_asc">Nome (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {reportType === 'leads' && (
            <div>
              <Label>Status do Lead</Label>
              <Select value={leadStatusFilter} onValueChange={setLeadStatusFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="new">Novo</SelectItem>
                  <SelectItem value="handled">Tratado</SelectItem>
                  <SelectItem value="spam">Spam</SelectItem>
                  <SelectItem value="converted">Convertidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {reportType === 'clients' && (
            <div>
              <Label>Status do Cliente</Label>
              <Select value={clientStatusFilter} onValueChange={setClientStatusFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="new">Novo</SelectItem>
                  <SelectItem value="contacted">Contatado</SelectItem>
                  <SelectItem value="visit_scheduled">Visita Agendada</SelectItem>
                  <SelectItem value="proposal">Proposta</SelectItem>
                  <SelectItem value="closed_won">Fechado (Ganho)</SelectItem>
                  <SelectItem value="closed_lost">Fechado (Perdido)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleGenerateReport}
              className="inline-flex items-center rounded-md bg-brand-blue hover:bg-brand-blue-dark text-white px-4 py-2 transition-colors"
            >
              Gerar Relatório
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Resultado</CardTitle>

          {reportGenerated && (reportType === 'leads' || reportType === 'clients' || reportType === 'sales') && (
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center rounded-md border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 text-sm transition-colors"
            >
              Imprimir
            </button>
          )}
        </CardHeader>

        <CardContent>
          {!reportGenerated && (
            <p className="text-slate-500">
              Defina os filtros e clique em <strong>Gerar Relatório</strong>.
            </p>
          )}

          {reportType === 'leads' && reportGenerated && (
            <div id="print-report" className="overflow-x-auto">
              {loading ? (
                <p className="text-slate-500">Carregando...</p>
              ) : (
                <>
                  <div className="mb-4 print:block">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-32">
                        <img
                          src="https://customer-assets.emergentagent.com/job_toledo-realestate/artifacts/oy9vxwiq_01.png"
                          alt="Logo Valmir Capeletti"
                          className="h-28 w-auto"
                        />
                      </div>

                      <div className="flex-1 text-center">
                        <div className="bg-slate-100 border border-slate-300 py-2 px-4 font-semibold text-slate-800">
                          RELATÓRIO DE LEADS
                        </div>
                      </div>

                      <div className="w-32" />
                    </div>

                    <div className="text-sm text-slate-500 mb-3">
                      {startDate || endDate ? (
                        <span>
                          Período:{' '}
                            {startDate ? formatDateBR(startDate) : 'Início'} até {endDate ? formatDateBR(endDate) : 'Hoje'}
                        </span>
                      ) : (
                        <span>Período: Todos</span>
                      )}
                    </div>
                  </div>

                  <table className="w-full border border-slate-300 text-sm">
                      <thead>
                        <tr>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[14%]">
                            Data
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[28%]">
                            Nome
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[20%]">
                            Telefone
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[22%]">
                            Origem
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[16%]">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredData().map((item, index) => (
                          <tr
                            key={item.id}
                            className={index % 2 === 0 ? 'bg-white' : 'bg-sky-50'}
                          >
                            <td className="p-2 border border-slate-300">
                              {item.created_at
                                ? new Date(item.created_at).toLocaleDateString('pt-BR', {
                                  timeZone: 'America/Sao_Paulo',
                                })
                                : '-'}
                            </td>
                            <td className="p-2 border border-slate-300">{item.name || '-'}</td>
                            <td className="p-2 border border-slate-300">{item.phone || '-'}</td>
                            <td className="p-2 border border-slate-300">
                              {item.page_source || '-'}
                            </td>
                            <td className="p-2 border border-slate-300">
                              {leadStatusLabels[item.status] || item.status || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {reportType === 'clients' && reportGenerated && (
            <div id="print-report" className="overflow-x-auto">
              {loading ? (
                <p className="text-slate-500">Carregando...</p>
              ) : (
                <>
                  <div className="mb-4 print:block">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-32">
                        <img
                          src="https://customer-assets.emergentagent.com/job_toledo-realestate/artifacts/oy9vxwiq_01.png"
                          alt="Logo Valmir Capeletti"
                          className="h-28 w-auto"
                        />
                      </div>

                      <div className="flex-1 text-center">
                        <div className="bg-slate-100 border border-slate-300 py-2 px-4 font-semibold text-slate-800">
                          RELATÓRIO DE CLIENTES
                        </div>
                      </div>

                      <div className="w-32" />
                    </div>

                    <div className="text-sm text-slate-500 mb-3">
                      {startDate || endDate ? (
                        <span>
                          Período:{' '}
                            {startDate ? formatDateBR(startDate) : 'Início'} até {endDate ? formatDateBR(endDate) : 'Hoje'}
                        </span>
                      ) : (
                        <span>Período: Todos</span>
                      )}
                    </div>
                  </div>

                  <table className="w-full border border-slate-300 text-sm">
                    <thead>
                        <tr>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[10%]">
                            Data
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[16%]">
                            Nome
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[14%]">
                            Telefone
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[14%]">
                            Status
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-center w-[6%]">
                            Interações
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[10%]">
                            Data
                          </th>
                          <th className="p-2 border border-slate-300 bg-slate-100 text-left w-[30%]">
                            Interação
                          </th>
                        </tr>
                    </thead>
                    <tbody>
                      {getFilteredData().map((item, index) => (
                        <tr
                          key={item.id}
                          className={index % 2 === 0 ? 'bg-white' : 'bg-sky-50'}
                        >
                          <td className="p-2 border border-slate-300">
                            {item.created_at
                              ? new Date(item.created_at).toLocaleDateString('pt-BR', {
                                timeZone: 'America/Sao_Paulo',
                              })
                              : '-'}
                          </td>

                          <td className="p-2 border border-slate-300">{item.name || '-'}</td>

                          <td className="p-2 border border-slate-300">{item.phone || '-'}</td>

                          <td className="p-2 border border-slate-300">
                            {clientStatusLabels[item.status] || item.status || '-'}
                          </td>

                          <td className="p-2 border border-slate-300 text-center">
                            {item.interactions_count || 0}
                          </td>

                          <td className="p-2 border border-slate-300">
                            {item.last_interaction
                              ? new Date(item.last_interaction).toLocaleDateString('pt-BR', {
                                timeZone: 'America/Sao_Paulo',
                              })
                              : '-'}
                          </td>

                          <td className="p-2 border border-slate-300 text-[12px] leading-5">
                            {item.last_interaction_text || 'Sem interação'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {reportType === 'sales' && reportGenerated && (
            <div id="print-report" className="overflow-x-auto">
              {loading ? (
                <p className="text-slate-500">Carregando...</p>
              ) : (
                <>
                  {(() => {
                    const summary = getSalesSummary();

                    return (
                      <>
                        <div className="mb-4 print:block">
                          <div className="flex items-center justify-between mb-3">
                            <div className="w-32">
                              <img
                                src="https://customer-assets.emergentagent.com/job_toledo-realestate/artifacts/oy9vxwiq_01.png"
                                alt="Logo Valmir Capeletti"
                                className="h-28 w-auto"
                              />
                            </div>

                            <div className="flex-1 text-center">
                              <div className="bg-slate-100 border border-slate-300 py-2 px-4 font-semibold text-slate-800">
                                RELATÓRIO DE VENDAS
                              </div>
                            </div>

                            <div className="w-32" />
                          </div>

                          <div className="text-sm text-slate-500 mb-3">
                            {startDate || endDate ? (
                              <span>
                                Período:{' '}
                                {startDate ? formatDateBR(startDate) : 'Início'} até {endDate ? formatDateBR(endDate) : 'Hoje'}
                              </span>
                            ) : (
                              <span>Período: Todos</span>
                            )}
                          </div>
                        </div>

                        {/* RESUMO */}
                        <div className="grid md:grid-cols-3 gap-4 mb-6">
                          <Card>
                            <CardContent className="p-4">
                              <p className="text-sm text-slate-500">Vendas</p>
                              <p className="text-xl font-bold">{summary.count}</p>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardContent className="p-4">
                              <p className="text-sm text-slate-500">Valor Vendido</p>
                              <p className="text-xl font-bold text-green-600">
                                {formatCurrency(summary.totalSales)}
                              </p>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardContent className="p-4">
                              <p className="text-sm text-slate-500">Comissão</p>
                              <p className="text-xl font-bold text-blue-600">
                                {formatCurrency(summary.totalCommission)}
                              </p>
                            </CardContent>
                          </Card>
                        </div>

                        {/* TABELA */}
                        <table className="w-full border border-slate-300 text-sm">
                          <thead>
                            <tr>
                              <th className="p-2 border">Data do Negócio</th>
                              <th className="p-2 border">ID Imóvel</th>
                              <th className="p-2 border">Título do Imóvel</th>
                              <th className="p-2 border">Cliente</th>
                              <th className="p-2 border">Valor Venda</th>
                              <th className="p-2 border">Comissão</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredData().map((item) => (
                              <tr key={item.id}>
                                <td className="p-2 border">
                                  {item.deal_date
                                    ? new Date(item.deal_date).toLocaleDateString('pt-BR')
                                    : '-'}
                                </td>

                                <td className="p-2 border">
                                  {item.property_id || '-'}
                                </td>

                                <td className="p-2 border">
                                  {item.property_title || '-'}
                                </td>

                                <td className="p-2 border">
                                  {item.client_name || '-'}
                                </td>

                                <td className="p-2 border text-green-600 font-medium">
                                  {formatCurrency(item.sale_value)}
                                </td>

                                <td className="p-2 border text-blue-600 font-medium">
                                  {formatCurrency(item.commission_value)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}